import React, { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { InputField } from "../../../../Components/formElements/InputField";

const ERFAccessoriesSection = ({
  rentItems = [],
  saleItems = [],
  accessoryItems = [],
  isStoreUser = false,
  isAdmin = false,
  status = "",
  erfStatusCode="",
  isSaving = false,
  onOpenModal,
  onSaveAccessories,
  onBarcodeChange,
  onBarcodeSubmit,
  onBarcodeAssign,
  onRemoveAccessory,
  assigningBarcode = {},
  onOpenScanModal,
  isAssigningBarcode = false, 
}) => {
  // ── Determine if there are unsaved accessories (barcode null = not yet saved/assigned) ──
  const canManageAccessories = (isStoreUser || isAdmin) && status === "Approved" && erfStatusCode==="ATS";
  //   const hasUnsavedAccessories = accessoryItems.some((a) => !a.assignBarcode);
  const hasUnsavedAccessories = accessoryItems.some((a) => !a.savedToBackend);

  // ── Table columns ──────────────────────────────────────────────────────────
  const accessoryColumns = useMemo(
    () => [
      {
        name: "Product Code",
        cell: (row) => (
          <span className="fw-bold text-primary">
            {row.materialCode || "--"}
          </span>
        ),
        width: "150px",
        sortable: true,
      },
      {
        name: "Product Details",
        cell: (row) => (
          <span className="text-wrap small">
            {row.materialDescription || "--"}
          </span>
        ),
        width: "280px",
        wrap: true,
      },
      {
        name: "Brand",
        cell: (row) => <span>{row.brand || "--"}</span>,
        width: "120px",
      },
      {
        name: "HSN Code",
        cell: (row) => <span>{row.hsnCode || "--"}</span>,
        width: "110px",
      },
      // ── Scan Barcode icon (store user only) ─────────────────────────────────
      {
        name: "Scan Barcode",
        cell: (row) => {
          if (!isStoreUser) return null;
          return row.assignBarcode ? (
            <button
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#7fc5c4] transition-all duration-150 ease-in-out hover:bg-[#5fa9a8] hover:scale-105"
              disabled={true}
            >
              <i className="bi bi-camera-video-off text-xl"></i>
            </button>
          ) : (
            <button
              className="w-10 h-10 flex items-center justify-center rounded-full bg-[#7fc5c4] transition-all duration-150 ease-in-out hover:bg-[#5fa9a8] hover:scale-105"
              onClick={() => onOpenScanModal(row)}
              disabled={!row.savedToBackend}
              title={
                !row.savedToBackend ? "Save accessories first" : "Scan barcode"
              }
            >
              <i className="bi bi-upc-scan text-white text-xl"></i>
            </button>
          );
        },

        width: "130px",
        omit: !isStoreUser,
      },
      // ── Barcode input (store user only) ─────────────────────────────────────
      {
        name: "Barcode Value",
        cell: (row) => {
          if (!isStoreUser) {
            return (
              <span className="fw-bold text-primary">
                {row?.barcode || "--"}
              </span>
            );
          }
          return (
            // Always render InputField — same as rent/sale table
            // disabled when assigned OR not yet saved to backend
            <InputField
              className="inputBox"
              type="text"
              name="barcode"
              value={row?.barcode || ""}
              // disabled={row?.assignBarcode || !row.savedToBackend}
              disabled={true}
              placeholder={
                row.assignBarcode
                  ? ""
                  : !row.savedToBackend
                    ? "Save first"
                    : "Scan / enter barcode"
              }
              onChange={(e) => onBarcodeChange(row.local_id, e.target.value)}
              onBlur={(e) => onBarcodeSubmit(row, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onBarcodeSubmit(row, e.target.value);
                }
              }}
            />
          );
        },
        width: "220px",
        omit: false,
      },
      {
        name: "Assign",
        cell: (row) => (
          <>
            {row?.assignBarcode ? (
              // Mirrors rent table "Assigned" button
              <button className="btn btnTable" disabled={true}>
                Assigned
              </button>
            ) : (
              <button
                className="btn btnTable"
                onClick={() => onBarcodeAssign(row)}
                disabled={
                  !row.barcodeValidated ||
                  !row.savedToBackend ||
                  !row.equipmentItemNr ||
                  assigningBarcode[row.local_id] ||
                  isAssigningBarcode
                }
                title={
                  !row.savedToBackend || !row.equipmentItemNr
                    ? "Click 'Update Accessories' first"
                    : !row.barcodeValidated
                      ? "Enter and validate barcode first"
                      : "Assign barcode"
                }
              >
                {assigningBarcode[row.local_id] ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  "Assign"
                )}
              </button>
            )}
          </>
        ),
        width: "160px",
        omit: !isStoreUser,
      },
      // ── Remove — disabled for saved items ─────────────────────────────────

      {
        name: "Remove",
        cell: (row) => (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => onRemoveAccessory(row.local_id)}
            disabled={!!row.assignBarcode}
            title={
              row.assignBarcode
                ? "Cannot remove — barcode is assigned. Remove barcode first."
                : "Remove accessory"
            }
            style={{
              opacity: row.assignBarcode ? 0.4 : 1,
              cursor: row.assignBarcode ? "not-allowed" : "pointer",
            }}
          >
            <i className="bi bi-trash3"></i>
          </button>
        ),
        width: "120px",
        // center: true,
        style: { justifyContent: "center" },
        omit: !(isStoreUser || isAdmin),
      },
    ],
    [
      isStoreUser,
      isAdmin,
      onBarcodeChange,
      onBarcodeSubmit,
      onBarcodeAssign,
      onRemoveAccessory,
      assigningBarcode,
      onOpenScanModal,
      isAssigningBarcode
    ],
  );

  // ── Section only renders when at least one rent item has accessories: true
  const eligibleRentItems = rentItems.filter(
    (r) => r.accessories === true && r.accessoriesCode?.length > 0,
  );
  const eligibleSaleItems = saleItems.filter(
    // ← ADD
    (r) => r.accessories === true && r.accessoriesCode?.length > 0,
  );
  const allEligibleItems = [...eligibleRentItems, ...eligibleSaleItems];

  if (allEligibleItems.length === 0) {
    return null;
  }

  return (
    <div className="accordion mb-4" id="accordionAccessories">
      <div className="accordion-item">
        <h2 className="accordion-header">
          <Row className="accordion-button">
            <Col xs={1} className="text-left arrowBtn">
              <i className="bi bi-chevron-down"></i>
            </Col>
            <Col xs={1} className="text-center logoBtn">
              <span className="iconLogo">AC</span>
            </Col>
            <Col xs={7} md={7} lg={7}>
              <span className="headerTitle">Accessories</span>
              <span className="subHeaderTitle">
                Accessories linked to Items
              </span>
            </Col>
            <Col
              xs={3}
              md={3}
              lg={3}
              className="text-end pe-3 d-flex align-items-center justify-content-end gap-2"
            >
              {/* ── UPDATE BUTTON — only when ATS + store user ── */}
              {canManageAccessories &&
                accessoryItems.length > 0 &&
                hasUnsavedAccessories && (
                  <Col
                    xs={8}
                    md={8}
                    lg={10}
                    className="commTopButtonRightLeftPadding"
                  >
                    <button
                      className="btnTable"
                      onClick={onSaveAccessories}
                      disabled={isSaving}
                      title="Save accessories to ERF and enable barcode assignment"
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" />
                          Saving...
                        </>
                      ) : (
                        <>Update Accessories</>
                      )}
                    </button>
                  </Col>
                )}
            </Col>
          </Row>
        </h2>

        <div className="accordion-collapse collapse show visible">
          <div className="accordion-body">
            {/* One card per eligible rent item */}
            {allEligibleItems.map((item) => {
              const itemAccessories = accessoryItems.filter(
                (a) => a.parentLocalId === item.local_id,
              );

              return (
                <div
                  key={item.local_id}
                  className="border rounded mb-4 overflow-hidden"
                >
                  {/* Rent Item Header */}
                  <div
                    className={`d-flex justify-content-between align-items-center bg-light p-2 border rounded-top`}
                  >
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <span className="fw-bold text-primary">
                        {item.materialCode}
                      </span>
                      <span className="text-dark">
                        {item.materialDescription}
                      </span>
                    </div>

                    {/* Add Accessories button — store user only */}
                    {canManageAccessories && (
                      <Col
                        xs={4}
                        md={4}
                        lg={2}
                        className="commTopButtonRightLeftPadding"
                      >
                        <button
                          className="btnTable"
                          onClick={() => onOpenModal(item)}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <i className="bi bi-plus-lg me-1"></i>
                          Add Accessories
                        </button>
                      </Col>
                    )}
                  </div>

                  {/* Accessories Table */}
                  {itemAccessories.length > 0 ? (
                    <>
                      {/* Info bar: remind user to click Update before assigning barcode */}
                      {itemAccessories.some((a) => !a.savedToBackend) && (
                        <div className="alert alert-info m-2 py-2 small d-flex align-items-center gap-2">
                          <i className="bi bi-info-circle-fill"></i>
                          Click{" "}
                          <strong className="mx-1">
                            Update Accessories
                          </strong>{" "}
                          at the top to save, then you can assign barcodes.
                        </div>
                      )}
                      <DataTable
                        columns={accessoryColumns}
                        data={itemAccessories}
                        noDataComponent={null}
                      />
                    </>
                  ) : (
                    <div className="text-center py-4 text-muted small">
                      No accessories added for this item.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERFAccessoriesSection;
